/// <amd-module name="VSSUI/Components/LabelGroup/EditableLabelGroup" />
import * as React from "react";
import * as Resources from "VSSUI/Resources.Label";
import { IEditableLabelGroupProps, WrappingBehavior } from "./LabelGroup.Props";
import { ILabelModel } from "../Label/Label.Props";
import { LabelGroup } from "./LabelGroup";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { IObservableArray, IObservableValue, ObservableValue, ObservableArray, ObservableLike } from "VSS/Core/Observable";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";

interface IEditableLabelGroupState {
    isInEditMode: boolean;
}

export class EditableLabelGroup extends React.Component<IEditableLabelGroupProps, IEditableLabelGroupState> {
    private static editableGroupCount: number = 0;

    private autocompleteRef: React.RefObject<Autocomplete> = React.createRef<Autocomplete>();
    private autocompleteValue: IObservableValue<string> = new ObservableValue<string>("");

    private focusOutTimeoutId: number;

    private groupId: string;

    // Using a boolean instead of stopPropagation here so that people get the events up the DOM tree
    // if they have onMouseDown bound on wrappers etc.
    private isMouseDownEventHandled: boolean = false;

    private labelGroupRef: React.RefObject<LabelGroup> = React.createRef<LabelGroup>();

    private renderTitleIconFlag: IObservableValue<boolean> = new ObservableValue<boolean>(false);

    private selectedLabelContents: IObservableArray<string> = new ObservableArray<string>();

    constructor(props: IEditableLabelGroupProps) {
        super(props);

        this.groupId = "editable-label-group-" + EditableLabelGroup.editableGroupCount;

        this.state = {
            isInEditMode: false
        };
    }

    public render() {
        const {
            addButtonText = Resources.AddLabelButtonText,
            className,
            customColors,
            disableColorPicker = false,
            disableMouseFocusOnLabels = false,
            duplicateLabelText,
            enableHoverStyles = true,
            labelProps,
            onLabelClick,
            shrinkToContents = false,
            title,
            useBlankZeroData = false,
            watermark = Resources.AddLabelWatermark
        } = this.props;

        const { isInEditMode } = this.state;
        const editClassName = isInEditMode ? "edit" : "";
        const actualWrappingBehavior = isInEditMode ? WrappingBehavior.freeFlow : this.props.wrappingBehavior;

        let bodyContent: JSX.Element;

        if (labelProps.length > 0 || isInEditMode) {
            bodyContent = (
                <div
                    className={css("bolt-labelgroup--editableWrapper", editClassName, !shrinkToContents && "default-padding")}
                    onKeyDown={this.onWrapperKeyDown}
                    onMouseDown={this.onInnerMouseDOwn}
                >
                    <LabelGroup
                        ref={this.labelGroupRef}
                        {...this.props}
                        className={undefined}
                        id={getSafeId(this.groupId)}
                        onLabelMouseDown={this.onLabelMouseDown}
                        onLabelKeyDown={this.onLabelKeyDown}
                        onLabelClick={onLabelClick}
                        selectedLabelContents={this.selectedLabelContents}
                        title={undefined}
                        disableMouseFocusOnLabels={disableMouseFocusOnLabels}
                        enableHoverStyles={enableHoverStyles}
                        wrappingBehavior={actualWrappingBehavior}
                    >
                        {isInEditMode && (
                            <Autocomplete
                                ref={this.autocompleteRef}
                                suggestionProvider={this.onGetSuggestions}
                                value={this.autocompleteValue}
                                placeholder={watermark}
                                onKeyDown={this.onInputKeyDown}
                                onSubmit={this.onInputSubmit}
                                onFocus={this.onAutocompleteFocus}
                                onInputValueChange={this.onAutocompleteValueChange}
                                customColors={customColors}
                                onCheckForDuplicateInParent={this.onCheckForDuplicates}
                                onDuplicateInParentText={duplicateLabelText}
                                disableColorPicker={disableColorPicker}
                                ariaDescribedBy={getSafeId(this.groupId)}
                            />
                        )}
                    </LabelGroup>
                </div>
            );
        } else if (!useBlankZeroData) {
            bodyContent = (
                <Button
                    className="bolt-labelgroup-addButton"
                    text={addButtonText}
                    iconProps={{ iconName: "Add" }}
                    onClick={this.onAddButtonClicked}
                />
            );
        } else {
            // If we have zero data and are using the blank one, render nothing at all
            return null;
        }

        return (
            <div
                className={css(className, "bolt-labelgroup-editable flex-column", editClassName)}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                onMouseDown={this.onOuterMouseDown}
                onMouseEnter={() => (this.renderTitleIconFlag.value = isInEditMode ? false : true)}
                onMouseLeave={() => (this.renderTitleIconFlag.value = false)}
            >
                {title && (
                    <Observer renderIconFlag={this.renderTitleIconFlag}>
                        {(observerProps: { renderIconFlag: boolean }) => (
                            <div className="bolt-labelgroup-title-wrapper body-m flex-row">
                                <div className="bolt-labelgroup-title-content">{title}</div>
                                {observerProps.renderIconFlag && <Icon iconName="Edit" />}
                            </div>
                        )}
                    </Observer>
                )}
                {bodyContent}
            </div>
        );
    }

    public componentWillUnmount() {
        // Niche race condition, but let's not setstate on an unmounted component
        clearTimeout(this.focusOutTimeoutId);
    }

    public focus() {
        this.setState({ isInEditMode: true }, () => this.autocompleteRef.current && this.autocompleteRef.current.focus());
    }

    private filterLabelModelAgainstContents = (testModel: ILabelModel) => {
        return ObservableLike.getValue(this.props.labelProps).filter(y => y.content.toUpperCase() === testModel.content.toUpperCase()).length == 0;
    };

    private onAddButtonClicked = () => {
        this.setState({ isInEditMode: true }, () => this.autocompleteRef.current.focus());
    };

    private onAutocompleteFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        this.selectedLabelContents.removeAll();
    };

    private onAutocompleteValueChange = (newValue: string) => {
        this.autocompleteValue.value = newValue;
    };

    private onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        /**
         * Delay the toggle off; _onFocus will clear the timeout
         */
        this.focusOutTimeoutId = setTimeout(() => {
            this.selectedLabelContents.removeAll();
            this.autocompleteValue.value = "";
            this.setState({ isInEditMode: false });
            this.props.onBlur && this.props.onBlur();
        }, 0); /**/
    };

    private onCheckForDuplicates = (newInput: string) => {
        return (
            ObservableLike.getValue(this.props.labelProps).filter(
                (testModel: ILabelModel) => testModel.content.trim().toUpperCase() === newInput.trim().toUpperCase()
            ).length !== 0
        );
    };

    // Only for handling keyboard focus
    private onFocus = (event: React.FocusEvent<HTMLDivElement>) => {
        clearTimeout(this.focusOutTimeoutId);

        if (!this.state.isInEditMode) {
            const callbackFunction = () => {
                this.renderTitleIconFlag.value = false;
                this.autocompleteRef.current.focus();
            };
            this.setState({ isInEditMode: true }, callbackFunction);
        }
    };

    private onGetSuggestions = (content: string): PromiseLike<ILabelModel[]> => {
        if (content === "" || !this.props.getSuggestedLabels) {
            return new Promise(resolve => resolve([]));
        }

        const labelModels: ILabelModel[] | PromiseLike<ILabelModel[]> = this.props.getSuggestedLabels(content);
        return Promise.resolve(labelModels).then((suggestions: ILabelModel[]) => suggestions.filter(this.filterLabelModelAgainstContents));
    };

    private onInnerMouseDOwn = (event: React.MouseEvent<HTMLDivElement>) => {
        // Eat the event; allow focus to move and signal that the outer wrapper shouldn't handle it
        if (this.state.isInEditMode) {
            this.isMouseDownEventHandled = true;
        }
    };

    private onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const inputValue: string = this.autocompleteValue.value;

        if (event.which === KeyCode.backspace || event.which === KeyCode.delete) {
            if (this.props.labelProps.length > 0 && inputValue === "") {
                // Make sure we have an element to remove
                this.removeLabel(ObservableLike.getValue(this.props.labelProps)[this.props.labelProps.length - 1].content);
                event.preventDefault();
            }
        }
    };

    private onInputSubmit = (labelModel: ILabelModel) => {
        this.props.onLabelSubmit && this.props.onLabelSubmit(labelModel);
        this.autocompleteValue.value = "";
        this.autocompleteRef.current.focus();
    };

    private onLabelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, model: ILabelModel, index: number) => {
        this.props.onLabelKeyDown && this.props.onLabelKeyDown(event, model, index);

        if (event.isDefaultPrevented()) {
            return;
        }

        const selectedOrFocusedContents: Set<string> = new Set<string>([...this.selectedLabelContents.value, model.content]);

        if (event.which === KeyCode.backspace || event.which === KeyCode.delete) {
            let nextLabelFocusIndex: number = -1;

            let minSelectedIndex: number = 99999;
            selectedOrFocusedContents.forEach((content: string) => {
                const index: number = ObservableLike.getValue(this.props.labelProps).findIndex(
                    (testModel: ILabelModel) => testModel.content === content
                );
                minSelectedIndex = Math.min(minSelectedIndex, index);
            });

            // If it's 0, we need to focus the first unselected item in the group
            if (minSelectedIndex === 0) {
                const selectedNextContent = new Set<string>(selectedOrFocusedContents);
                nextLabelFocusIndex = ObservableLike.getValue(this.props.labelProps).findIndex(
                    (model: ILabelModel) => !selectedNextContent.has(model.content)
                );
            } else {
                // Otherwise focus the one before the earliest deleted
                nextLabelFocusIndex = minSelectedIndex - 1;
            }

            // If we didn't select a new element, focus the autocomplete
            if (nextLabelFocusIndex === -1) {
                this.autocompleteRef.current.focus();
            } else {
                // Otherwise set a timeout
                this.labelGroupRef.current.focusLabel(nextLabelFocusIndex);
            }

            selectedOrFocusedContents.forEach((content: string) => this.removeLabel(content));
            this.selectedLabelContents.removeAll();
        } else if (
            event.which === KeyCode.upArrow ||
            event.which === KeyCode.downArrow ||
            event.which === KeyCode.leftArrow ||
            event.which === KeyCode.rightArrow
        ) {
            this.selectedLabelContents.removeAll();
        }
    };

    private onLabelMouseDown = (event: React.MouseEvent<HTMLDivElement>, model: ILabelModel, index: number) => {
        this.props.onLabelMouseDown && this.props.onLabelMouseDown(event, model, index);

        if (event.isDefaultPrevented() || !this.state.isInEditMode) {
            return;
        }

        const selectedContentSet: Set<string> = new Set<string>(this.selectedLabelContents.value);

        // Handle multiselect
        if (event.ctrlKey) {
            if (selectedContentSet.has(model.content)) {
                this.selectedLabelContents.removeAll((value: string) => value === model.content);
            } else {
                this.selectedLabelContents.push(model.content);
            }
        } else {
            // Single Select
            this.selectedLabelContents.value = selectedContentSet.has(model.content) ? [] : [model.content];
        }
    };

    private onOuterMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        // If the inner container handled it, reset and ignore the event
        if (this.isMouseDownEventHandled) {
            this.isMouseDownEventHandled = false;
            return;
        }

        // Only go into edit mode if we're not already there
        if (!this.state.isInEditMode) {
            // Don't deal with mouse-related focus, we'll manage it manually
            event.preventDefault();

            // Focus move here is only good if we didn't eat the event in _innerMouseDown
            // meaning the click was truly on the OUTER containers
            const callbackFunction = () => {
                this.renderTitleIconFlag.value = false;
                this.autocompleteRef.current.focus();
            };
            this.setState({ isInEditMode: true }, callbackFunction);
        }
    };

    private onWrapperKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.which === KeyCode.escape) {
            this.selectedLabelContents.removeAll();
            this.autocompleteValue.value = "";
            this.setState({ isInEditMode: false });
            this.props.onBlur && this.props.onBlur();
            event.preventDefault();
        }
    };

    private removeLabel(content: string) {
        this.props.onLabelRemove &&
            this.props.onLabelRemove(ObservableLike.getValue(this.props.labelProps).find((testModel: ILabelModel) => testModel.content === content));
        this.selectedLabelContents.removeAll(x => x === content);
    }
}
